import { useState, useCallback } from 'react';
import { battlesApi } from '../../../api/battles';

/**
 * Хук для управления раундами баттла
 */
export const useBattleRounds = (battleId, fetchRounds) => {
  const [isRoundModalOpen, setIsRoundModalOpen] = useState(false);
  const [editingRoundId, setEditingRoundId] = useState(null);
  const [roundActionLoading, setRoundActionLoading] = useState({});
  const [expandedRoundDetails, setExpandedRoundDetails] = useState({});
  const [loadingRoundDetails, setLoadingRoundDetails] = useState({});

  const [roundForm, setRoundForm] = useState({
    title: '',
    description: '',
    theme: '',
    task: '',
    type: 'QUALIFYING',
    status: 'PREPARATION',
    number: 1,
    startTime: '',
    endTime: '',
    votingEnabled: false,
    votingEndTime: '',
    trackUploadDeadline: '',
    votingSystem: '',
    criteriaPreset: '',
    limitations: [],
    participants: [],
  });

  const [newRoundRule, setNewRoundRule] = useState({ title: '', description: '' });
  const [newRoundLimitation, setNewRoundLimitation] = useState({
    name: '',
    description: '',
    limitationType: 'TRACK_DURATION',
    minTrackDuration: null,
    maxTrackDuration: null,
    minTextLength: null,
    maxTextLength: null,
    maxFileSizeMb: null,
    allowedFormats: '',
    minAge: null,
    isActive: true,
  });

  /** Открыть модалку создания раунда */
  const openCreateRoundModal = useCallback((rounds = []) => {
    setEditingRoundId(null);
    setRoundForm({
      title: '',
      description: '',
      theme: '',
      task: '',
      type: 'QUALIFYING',
      status: 'PREPARATION',
      number: rounds.length + 1,
      startTime: '',
      endTime: '',
      votingEnabled: false,
      votingEndTime: '',
      trackUploadDeadline: '',
      votingSystem: '',
      criteriaPreset: '',
      limitations: [],
      participants: [],
    });
    setNewRoundLimitation({
      name: '',
      description: '',
      limitationType: 'TRACK_DURATION',
      minTrackDuration: null,
      maxTrackDuration: null,
      minTextLength: null,
      maxTextLength: null,
      maxFileSizeMb: null,
      allowedFormats: '',
      minAge: null,
      isActive: true,
    });
    setIsRoundModalOpen(true);
  }, []);

  /** Открыть модалку создания раунда из вкладки */
  const handleCreateRoundFromTab = useCallback(
    (tabId, rounds = []) => {
      const typeMap = {
        qualifying: 'QUALIFYING',
        draw: 'DRAW',
        table: 'PAIR',
      };

      const roundType = typeMap[tabId] || 'QUALIFYING';
      const nextNumber =
        rounds.length > 0
          ? Math.max(...rounds.map((r) => r.number || 0)) + 1
          : 1;

      setEditingRoundId(null);
      setRoundForm({
        title: '',
        description: '',
        theme: '',
        task: '',
        type: roundType,
        status: 'PREPARATION',
        number: nextNumber,
        startTime: '',
        endTime: '',
        votingEnabled: false,
        votingEndTime: '',
        trackUploadDeadline: '',
        votingSystem: '',
        criteriaPreset: '',
        limitations: [],
        participants: [],
      });
      setNewRoundLimitation({
        name: '',
        description: '',
        limitationType: 'TRACK_DURATION',
        minTrackDuration: null,
        maxTrackDuration: null,
        minTextLength: null,
        maxTextLength: null,
        maxFileSizeMb: null,
        allowedFormats: '',
        minAge: null,
        isActive: true,
      });
      setIsRoundModalOpen(true);
    },
    []
  );

  /** Открыть модалку редактирования раунда */
  const openEditRoundModal = useCallback((round) => {
    setEditingRoundId(round.id);
    setRoundForm({
      title: round.title || '',
      description: round.description || '',
      theme: round.theme || '',
      task: round.task || '',
      type: round.type || 'QUALIFYING',
      status: round.status || 'PREPARATION',
      number: round.number || 1,
      startTime: round.startTime
        ? round.startTime.replace('T', ' ').substring(0, 16)
        : '',
      endTime: round.endTime
        ? round.endTime.replace('T', ' ').substring(0, 16)
        : '',
      votingEnabled: round.votingEnabled || false,
      votingEndTime: round.votingEndTime
        ? round.votingEndTime.replace('T', ' ').substring(0, 16)
        : '',
      trackUploadDeadline: round.trackUploadDeadline
        ? round.trackUploadDeadline.replace('T', ' ').substring(0, 16)
        : '',
      votingSystem: round.votingSystem || '',
      criteriaPreset: round.criteriaPreset || '',
      limitations: round.limitations || [],
      participants: (round.participants || []).map((p) => p.id),
    });
    setIsRoundModalOpen(true);
  }, []);

  /** Закрыть модалку */
  const closeRoundModal = useCallback(() => {
    setIsRoundModalOpen(false);
    setEditingRoundId(null);
  }, []);

  /** Изменение поля формы */
  const handleRoundFormChange = useCallback((field, value) => {
    setRoundForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** Создание раунда */
  const handleCreateRound = useCallback(
    async (e) => {
      e.preventDefault();
      if (!roundForm.title || !roundForm.startTime || !roundForm.endTime) {
        throw new Error('Заполните обязательные поля раунда');
      }
      // Валидация: если голосование включено, нужно указать время окончания
      if (roundForm.votingEnabled && !roundForm.votingEndTime) {
        throw new Error('Укажите время окончания голосования');
      }
      try {
        setRoundActionLoading((prev) => ({ ...prev, create: true }));

        // Формируем правильный payload для API
        const payload = {
          title: roundForm.title.trim(),
          description: roundForm.description?.trim() || null,
          theme: roundForm.theme?.trim() || null,
          task: roundForm.task?.trim() || null,
          type: roundForm.type,
          status: roundForm.status || 'PREPARATION',
          number: parseInt(roundForm.number) || 1,
          startTime: roundForm.startTime,
          endTime: roundForm.endTime,
          votingEnabled: roundForm.votingEnabled || false,
          votingEndTime: roundForm.votingEnabled ? (roundForm.votingEndTime || null) : null,
          trackUploadDeadline: roundForm.trackUploadDeadline || null,
          // Новые поля для систем голосования
          votingSystem: roundForm.votingSystem || undefined,
          criteriaPreset: roundForm.criteriaPreset || undefined,
          // limitations передаём только если массив не пустой
          ...(roundForm.limitations && roundForm.limitations.length > 0 && {
            limitations: roundForm.limitations
          }),
          // participants передаём только если массив не пустой
          ...(roundForm.participants && roundForm.participants.length > 0 && {
            participants: roundForm.participants
          }),
        };

        console.log('[useBattleRounds] Creating round with payload:', payload);

        await battlesApi.createRound(battleId, payload);
        await fetchRounds();
        closeRoundModal();
      } catch (err) {
        console.error('[useBattleRounds] Failed to create round:', err);
        console.error('[useBattleRounds] Error response:', err.response?.data);
        console.error('[useBattleRounds] Error status:', err.response?.status);
        console.error('[useBattleRounds] Error headers:', err.response?.headers);
        // Пробрасываем ошибку дальше для отображения пользователю
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({ ...prev, create: false }));
      }
    },
    [battleId, roundForm, fetchRounds, closeRoundModal]
  );

  /** Обновление раунда */
  const handleUpdateRound = useCallback(
    async (e) => {
      e.preventDefault();
      if (!editingRoundId) return;
      // Валидация: если голосование включено, нужно указать время окончания
      if (roundForm.votingEnabled && !roundForm.votingEndTime) {
        throw new Error('Укажите время окончания голосования');
      }
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [editingRoundId]: true,
        }));

        // Формируем правильный payload для API
        const payload = {
          title: roundForm.title.trim(),
          description: roundForm.description?.trim() || null,
          theme: roundForm.theme?.trim() || null,
          task: roundForm.task?.trim() || null,
          type: roundForm.type,
          status: roundForm.status || 'PREPARATION',
          number: parseInt(roundForm.number) || 1,
          startTime: roundForm.startTime,
          endTime: roundForm.endTime,
          votingEnabled: roundForm.votingEnabled || false,
          votingEndTime: roundForm.votingEnabled ? (roundForm.votingEndTime || null) : null,
          trackUploadDeadline: roundForm.trackUploadDeadline || null,
          // Новые поля для систем голосования
          votingSystem: roundForm.votingSystem || undefined,
          criteriaPreset: roundForm.criteriaPreset || undefined,
          // limitations передаём только если массив не пустой
          ...(roundForm.limitations && roundForm.limitations.length > 0 && {
            limitations: roundForm.limitations
          }),
          // participants передаём только если массив не пустой
          ...(roundForm.participants && roundForm.participants.length > 0 && {
            participants: roundForm.participants
          }),
        };

        console.log('[useBattleRounds] Updating round:', editingRoundId, 'with payload:', payload);

        await battlesApi.updateRound(battleId, editingRoundId, payload);
        await fetchRounds();
        closeRoundModal();
      } catch (err) {
        console.error('[useBattleRounds] Failed to update round:', err);
        console.error('[useBattleRounds] Error response:', err.response?.data);
        console.error('[useBattleRounds] Error status:', err.response?.status);
        // Пробрасываем ошибку дальше для отображения пользователю
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [editingRoundId]: false,
        }));
      }
    },
    [battleId, editingRoundId, roundForm, fetchRounds, closeRoundModal]
  );

  /** Удаление раунда */
  const handleDeleteRound = useCallback(
    async (roundId) => {
      if (!window.confirm('Вы уверены, что хотите удалить этот раунд?')) return;
      try {
        setRoundActionLoading((prev) => ({ ...prev, [roundId]: true }));
        await battlesApi.deleteRound(battleId, roundId);
        await fetchRounds();
      } catch (err) {
        console.error('[useBattleRounds] Failed to delete round:', err.message);
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({ ...prev, [roundId]: false }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Обновление статуса раунда */
  const handleUpdateRoundStatus = useCallback(
    async (roundId, newStatus) => {
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`status-${roundId}`]: true,
        }));
        await battlesApi.updateRoundStatus(battleId, roundId, newStatus);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to update round status:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`status-${roundId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Переключение деталей раунда */
  const toggleRoundDetails = useCallback(
    (roundId, fetchRoundRules, fetchRoundLimitations) => {
      console.log(`[useBattleRounds] toggleRoundDetails called for round ${roundId}`);
      console.log(`[useBattleRounds] fetchRoundRules:`, !!fetchRoundRules, `fetchRoundLimitations:`, !!fetchRoundLimitations);

      setExpandedRoundDetails((prev) => {
        const next = { ...prev, [roundId]: !prev[roundId] };
        console.log(`[useBattleRounds] Setting expandedRoundDetails:`, next);

        if (next[roundId]) {
          // Устанавливаем состояние загрузки
          setLoadingRoundDetails((prev) => ({ ...prev, [roundId]: true }));
          console.log(`[useBattleRounds] Setting loadingRoundDetails[${roundId}] = true`);

          // Загружаем данные
          if (fetchRoundRules) {
            console.log(`[useBattleRounds] Calling fetchRoundRules(${roundId})`);
            fetchRoundRules(roundId);
          }
          if (fetchRoundLimitations) {
            console.log(`[useBattleRounds] Calling fetchRoundLimitations(${roundId})`);
            fetchRoundLimitations(roundId);
          }

          // Снимаем состояние загрузки (с небольшой задержкой, чтобы данные успели загрузиться)
          setTimeout(() => {
            setLoadingRoundDetails((prev) => ({ ...prev, [roundId]: false }));
            console.log(`[useBattleRounds] Setting loadingRoundDetails[${roundId}] = false`);
          }, 300);
        }
        return next;
      });
    },
    []
  );

  /** Добавить правило раунда */
  const handleAddRoundRule = useCallback(
    async (roundId) => {
      if (!newRoundRule.title.trim()) {
        throw new Error('Введите название правила');
      }
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-rule-${roundId}`]: true,
        }));
        await battlesApi.createRoundRule(battleId, roundId, {
          title: newRoundRule.title.trim(),
          description: newRoundRule.description.trim() || null,
        });
        await fetchRounds();
        setNewRoundRule({ title: '', description: '' });
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to add round rule:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-rule-${roundId}`]: false,
        }));
      }
    },
    [battleId, newRoundRule, fetchRounds]
  );

  /** Удалить правило раунда */
  const handleDeleteRoundRule = useCallback(
    async (roundId, ruleId) => {
      if (!window.confirm('Удалить это правило?')) return;
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`del-rule-${ruleId}`]: true,
        }));
        await battlesApi.deleteRoundRule(battleId, roundId, ruleId);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to delete round rule:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`del-rule-${ruleId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Изменение поля формы нового ограничения */
  const handleNewLimitationChange = useCallback((field, value) => {
    setNewRoundLimitation((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** Добавить ограничение в форму */
  const handleAddLimitationToForm = useCallback(() => {
    if (!newRoundLimitation.name.trim()) {
      throw new Error('Введите название ограничения');
    }

    setRoundForm((prev) => ({
      ...prev,
      limitations: [
        ...prev.limitations,
        {
          name: newRoundLimitation.name.trim(),
          description: newRoundLimitation.description?.trim() || null,
          limitationType: newRoundLimitation.limitationType,
          minTrackDuration: newRoundLimitation.minTrackDuration,
          maxTrackDuration: newRoundLimitation.maxTrackDuration,
          minTextLength: newRoundLimitation.minTextLength,
          maxTextLength: newRoundLimitation.maxTextLength,
          maxFileSizeMb: newRoundLimitation.maxFileSizeMb,
          allowedFormats: newRoundLimitation.allowedFormats?.trim() || null,
          minAge: newRoundLimitation.minAge,
          isActive: true,
        },
      ],
    }));
    // Сброс формы нового ограничения
    setNewRoundLimitation({
      name: '',
      description: '',
      limitationType: 'TRACK_DURATION',
      minTrackDuration: null,
      maxTrackDuration: null,
      minTextLength: null,
      maxTextLength: null,
      maxFileSizeMb: null,
      allowedFormats: '',
      minAge: null,
      isActive: true,
    });
  }, [newRoundLimitation]);

  /** Удалить ограничение из формы */
  const handleRemoveLimitationFromForm = useCallback((index) => {
    setRoundForm((prev) => ({
      ...prev,
      limitations: prev.limitations.filter((_, i) => i !== index),
    }));
  }, []);

  /** Добавить участника в раунд */
  const handleAddRoundParticipant = useCallback(
    async (roundId, personId) => {
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-participant-${roundId}-${personId}`]: true,
        }));
        // API принимает массив participantIds
        await battlesApi.addRoundParticipants(battleId, roundId, [personId]);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to add round participant:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-participant-${roundId}-${personId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Удалить участника из раунда */
  const handleRemoveRoundParticipant = useCallback(
    async (roundId, personId) => {
      if (!window.confirm('Удалить участника из раунда?')) return;
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`remove-participant-${roundId}-${personId}`]: true,
        }));
        await battlesApi.removeRoundParticipant(battleId, roundId, personId);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to remove round participant:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`remove-participant-${roundId}-${personId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  return {
    // State
    isRoundModalOpen,
    editingRoundId,
    roundActionLoading,
    expandedRoundDetails,
    loadingRoundDetails,
    roundForm,
    newRoundRule,
    newRoundLimitation,

    // Setters
    setRoundForm,
    setNewRoundRule,
    handleNewLimitationChange,

    // Actions
    openCreateRoundModal,
    handleCreateRoundFromTab,
    openEditRoundModal,
    closeRoundModal,
    handleRoundFormChange,
    handleCreateRound,
    handleUpdateRound,
    handleDeleteRound,
    handleUpdateRoundStatus,
    toggleRoundDetails,
    handleAddRoundRule,
    handleDeleteRoundRule,
    handleAddLimitationToForm,
    handleRemoveLimitationFromForm,
    handleAddRoundParticipant,
    handleRemoveRoundParticipant,
  };
};
